/**
 * SocialProofBadge — small "Booked Nm ago" or "X watching now" pill.
 *
 * Iter 69 — shown on BrowseCarCard photo overlay and CarDetail booking
 * panel to stimulate booking intent without resorting to fake urgency.
 *
 * Priority:
 *   1. If `last_booked_minutes_ago` is set → render a green "Booked Nm ago"
 *      (REAL data — only set when a confirmed booking exists in last 48h).
 *   2. Otherwise if `viewers_now >= 2` → render an amber animated-dot
 *      "X watching now" (pseudo-deterministic, see backend).
 *   3. Else → render nothing.
 *
 * `size`: "sm" (card overlay) | "lg" (detail page sidebar).
 */
import { Eye, Zap } from "lucide-react";

function formatBookedAgo(mins) {
  if (mins == null) return null;
  if (mins < 1) return "Booked just now";
  if (mins < 60) return `Booked ${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Booked ${hrs}h ago`;
  // We only render for ≤ 48h so 1-2 days max.
  const days = Math.floor(hrs / 24);
  return `Booked ${days}d ago`;
}

export default function SocialProofBadge({ proof, size = "sm" }) {
  if (!proof) return null;
  const { last_booked_minutes_ago, viewers_now } = proof;
  const isBooked = typeof last_booked_minutes_ago === "number" && last_booked_minutes_ago <= 60 * 48;
  const showViewers = !isBooked && typeof viewers_now === "number" && viewers_now >= 2;

  if (!isBooked && !showViewers) return null;

  const baseClasses =
    size === "lg"
      ? "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm"
      : "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-sm";

  if (isBooked) {
    return (
      <span
        className={`${baseClasses} bg-emerald-500/95 text-white ring-1 ring-emerald-600/30 backdrop-blur-sm`}
        data-testid="social-proof-booked"
      >
        <Zap className={size === "lg" ? "h-3.5 w-3.5" : "h-2.5 w-2.5"} />
        <span>{formatBookedAgo(last_booked_minutes_ago)}</span>
      </span>
    );
  }

  // Viewers — amber, with a subtle pulsing dot to imply "live".
  return (
    <span
      className={`${baseClasses} bg-amber-400/95 text-amber-950 ring-1 ring-amber-600/30 backdrop-blur-sm`}
      data-testid="social-proof-viewers"
    >
      <span className="relative inline-flex h-2 w-2 items-center justify-center">
        <span className="absolute inset-0 rounded-full bg-amber-700 animate-ping opacity-70" />
        <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-amber-700" />
      </span>
      <Eye className={size === "lg" ? "h-3.5 w-3.5" : "h-2.5 w-2.5"} />
      <span>
        {viewers_now} {size === "lg" ? "people watching now" : "watching"}
      </span>
    </span>
  );
}
